/* eslint-disable camelcase */
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { pageSelector } from 'selectors/page/pageSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import superChatActions from 'ai/actions/superChatActions';
import { superChatSelector } from 'ai/selectors/superChatSelector';
import sdnApi from 'ai/services/sdn';
import { getGENAIAnonymousId } from 'ai/utils/sessionStorage';
import { isGenAINewUIEnabledWithRCPS, getEcid } from 'ai/utils/aiBeautyChat';

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocaleResourceFile } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('ai/components/EntryPoint/locales', 'AIChat');

const { entryPointPrompts } = sdnApi;

const PAGE = 'PDP';
const localization = createStructuredSelector({
    new: getTextFromResource(getText, 'new'),
    aiChatInputPlaceholderPdp: getTextFromResource(getText, 'aiChatInputPlaceholderPdp')
});

const functions = (dispatch, _ownProps) => ({
    fetchEntryPointPromptsPdp: async (product, clientId, anonymousId) => {
        const { currentSku, productDetails, parentCategory } = product || {};
        const skuId = currentSku?.skuId;
        const productId = productDetails?.productId || product?.productId;
        const categoryId = parentCategory?.categoryId;

        if (!productId || !skuId) {
            Sephora.logger.error(
                'SuperChat: skipping entry-point prompts fetch without product_id or sku_id',
                { productId, skuId }
            );

            return null;
        }

        const brandName = productDetails?.brand?.displayName || '';
        const productName = productDetails?.displayName || '';
        const entityName = brandName && productName ? `${brandName} - ${productName}` : brandName || productName;

        const payload = {
            productId,
            skuId,
            categoryId,
            entityName,
            page: PAGE,
            clientId,
            anonymousId,
            ecid: getEcid()
        };

        return entryPointPrompts(payload);
    },
    handlePromptClick: (prompts, product, selectedPrompt = null) => {
        const productId = product?.productId;
        const skuId = product?.currentSku?.skuId;

        if (!productId || !skuId) {
            Sephora.logger.error(
                'SuperChat: cannot open PDP chat without product_id or sku_id',
                { productId, skuId }
            );

            return;
        }

        const data = {
            entrypoint: 'PDP',
            products: [
                {
                    product_id: productId,
                    sku_id: skuId
                }
            ],
            selectedPrompt,
            source: 'pdp-entry'
        };

        dispatch(superChatActions.showSuperChat(true, prompts, data));
    },
    updatePrompts: prompts => {
        dispatch(superChatActions.setContextPrompts(prompts));
    }
});

const fields = createSelector(
    localization,
    coreUserDataSelector,
    pageSelector,
    superChatSelector,
    (_state, ownProps) => ownProps.giftFinder,
    (locales, user, page, superChat, giftFinder) => {
        const clientId = user?.biId;
        let anonymousId = undefined;

        if (!Sephora.isNodeRender) {
            anonymousId = getGENAIAnonymousId();
        }

        let chatName = superChat?.config?.meta?.try_chat_name || '';

        if (giftFinder) {
            chatName = superChat?.config?.meta?.try_gift_chat_name || '';
        }

        const isGenAINewUIEnabled = isGenAINewUIEnabledWithRCPS();

        return {
            localization: locales,
            product: page?.product || {},
            chatName: chatName,
            clientId,
            anonymousId,
            isRequestInFlight: superChat?.isRequestInFlight || false,
            isGenAINewUIEnabled
        };
    }
);

const withProductPageProps = wrapHOC(connect(fields, functions));

export {
    withProductPageProps, fields, functions
};
